// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { type AccountFormValues } from '@client/banks/hooks/use-account-form';
import { BankAccountType } from '@client/banks/types/account';
import { createEmptyOwnerRow } from '@client/banks/utils/bank-form-defaults';
import { getBankCurrencySymbol } from '@client/banks/utils/bank-currency';
import dayjs from 'dayjs';
import { useCallback, useMemo } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export type AccountFormOption = {
  value: string;
  label: string;
};

export type AccountFormProps = {
  formId: string;
  onSubmit: (data: AccountFormValues) => void | Promise<void>;
  currencyOptions: readonly AccountFormOption[];
  isIbanReadOnly?: boolean;
  layout?: 'default' | 'drawer';
  /** Show backend IBAN-generation tooltip (Liquidez tab only). */
  showIbanTooltip?: boolean;
  /** Mark code number as required in the label (create-linked flow). */
  ibanRequired?: boolean;
  /** Render without a nested `<form>` when embedded in another form. */
  embed?: boolean;
};

export const useAccountForm = ({
  currencyOptions: _currencyOptions,
  isIbanReadOnly = false,
  layout = 'default',
  showIbanTooltip = false,
  ibanRequired = false,
  embed = false,
  ..._
}: AccountFormProps) => {
  const { control, handleSubmit } = useFormContext<AccountFormValues>();

  const { t } = useTranslation();

  const ownersFieldArray = useFieldArray({
    control,
    name: 'owners',
  });

  const currency = useWatch({ control, name: 'currency' });

  const currencyPrefix = useMemo(() => getBankCurrencySymbol(currency), [currency]);

  const accountTypeOptions = useMemo(
    () =>
      Object.values(BankAccountType).map(value => ({
        value,
        label: t(`aggregator.product-type-value.${value}`, {
          ns: 'aggregator',
          defaultValue: value,
        }),
      })),
    [t],
  );

  const todayMaxDate = useMemo(() => dayjs().format('YYYY-MM-DD'), []);

  const onAddOwner = useCallback(() => {
    ownersFieldArray.prepend(createEmptyOwnerRow());
  }, [ownersFieldArray]);

  return {
    control,
    handleSubmit,
    t,
    currencyPrefix,
    ownersFieldArray,
    accountTypeOptions,
    todayMaxDate,
    onAddOwner,
    isIbanReadOnly,
    isDrawerLayout: layout === 'drawer',
    showIbanTooltip,
    ibanRequired,
    embed,
  };
};
